import React, { useState } from 'react';
import DataSelector from './DataSelector';
import ExportPanel from './ExportPanel';
import StylePanel from './StylePanel';
import PlaceSearch from './PlaceSearch';
import LegalModal, { LegalPage } from './LegalPages';
import LoadingParticles from './LoadingParticles';
import { DownloadIcon } from './Icons';
import { ExportOptions } from '../utils/paperSizes';
import { PresetKey, StyleMap, LayerStyle } from '../utils/exportStyles';
import { useI18n } from '../i18n/I18nContext';

import { OSMFeatureType } from './DataSelector';

interface SidebarCollapsibleProps {
  loading: boolean;
  loadingProgress: { done: number; total: number } | null;
  error: string | null;
  exportError: string | null;
  hasData: boolean;
  bboxAreaKm2: number;
  largeAreaKm2: number;
  selectedTypes: OSMFeatureType[];
  setSelectedTypes: (types: OSMFeatureType[]) => void;
  exportOptions: ExportOptions;
  setExportOptions: (opts: ExportOptions) => void;
  handleExport: (opts: ExportOptions) => void;
  handleExportSVG: (opts: ExportOptions) => void;
  handleExportDXF: (opts: ExportOptions) => void;
  onFetch: () => void;
  stylePreset: PresetKey;
  onStylePresetChange: (preset: PresetKey) => void;
  layerStyles: StyleMap;
  onLayerStyleChange: (type: OSMFeatureType, patch: Partial<LayerStyle>) => void;
  onPlaceSelect: (lon: number, lat: number) => void;
  contourInterval: number | null;
  onContourIntervalChange: (m: number | null) => void;
  usedContourInterval: number | null;
}

const SidebarCollapsible: React.FC<SidebarCollapsibleProps> = ({
  loading, loadingProgress, error, exportError, hasData, bboxAreaKm2, largeAreaKm2, selectedTypes, setSelectedTypes, exportOptions, setExportOptions, handleExport, handleExportSVG, handleExportDXF, onFetch,
  stylePreset, onStylePresetChange, layerStyles, onLayerStyleChange, onPlaceSelect,
  contourInterval, onContourIntervalChange, usedContourInterval,
}) => {
  const { t, lang, setLang } = useI18n();
  const [featuresOpen, setFeaturesOpen] = useState(true);
  const [exportOpen, setExportOpen] = useState(true);
  const [styleOpen, setStyleOpen] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [legalPage, setLegalPage] = useState<LegalPage | null>(null);

  return (
    <div className="qsp-sidebar">
      <div className="qsp-sidebar__header">
        <div className="qsp-sidebar__header-row">
          <h1 className="qsp-brand">Kontext</h1>
          <div className="qsp-lang-switch" role="group" aria-label={t('languageLabel')}>
            <button type="button" className={`qsp-lang-btn${lang === 'en' ? ' qsp-lang-btn--active' : ''}`} onClick={() => setLang('en')}>EN</button>
            <button type="button" className={`qsp-lang-btn${lang === 'de' ? ' qsp-lang-btn--active' : ''}`} onClick={() => setLang('de')}>DE</button>
          </div>
        </div>
        <p className="qsp-tagline">{t('tagline')}</p>
        <PlaceSearch onSelect={onPlaceSelect} />
      </div>

      <div className="qsp-section">
        <button type="button" className="qsp-section__head" onClick={() => setFeaturesOpen(f => !f)} aria-expanded={featuresOpen}>
          <span className="qsp-section__title">{t('featuresTitle')}</span>
          <span className={`qsp-section__chevron${featuresOpen ? ' qsp-section__chevron--open' : ''}`}>&#9656;</span>
        </button>
        {featuresOpen && (
          <div className="qsp-section__body">
            <DataSelector selected={selectedTypes} onChange={setSelectedTypes} />
            {selectedTypes.includes('contours') && (
              <label className="qsp-field" style={{ marginTop: 12 }}>
                <span className="qsp-field__label">{t('contourIntervalLabel')}</span>
                <input
                  className="qsp-input"
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={contourInterval ?? ''}
                  placeholder={t('contourIntervalAuto')}
                  onChange={e => {
                    const v = Number(e.target.value);
                    onContourIntervalChange(e.target.value === '' || !(v > 0) ? null : v);
                  }}
                />
                <span className="qsp-field__hint">
                  {usedContourInterval ? t('contourIntervalUsed', { m: usedContourInterval }) + ' · ' : ''}{t('contourSourceHint')}
                </span>
              </label>
            )}
            <button type="button" className="qsp-btn" style={{ marginTop: 14 }} onClick={onFetch} disabled={loading}>
              <DownloadIcon size={16} />
              {loading ? t('fetchButtonLoading') : t('fetchButton')}
            </button>
            {loading && (
              <LoadingParticles
                label={loadingProgress ? t('loadingTextProgress', { done: loadingProgress.done, total: loadingProgress.total }) : t('loadingText')}
              />
            )}
            {!loading && error && <div className="qsp-status qsp-status--error" style={{ margin: '14px 0 0' }}>{error}</div>}
          </div>
        )}
      </div>

      <div className="qsp-section">
        <button type="button" className="qsp-section__head" onClick={() => setExportOpen(e => !e)} aria-expanded={exportOpen}>
          <span className="qsp-section__title">{t('exportTitle')}</span>
          <span className={`qsp-section__chevron${exportOpen ? ' qsp-section__chevron--open' : ''}`}>&#9656;</span>
        </button>
        {exportOpen && (
          <div className="qsp-section__body">
            <ExportPanel
              onExport={handleExport}
              onExportSVG={handleExportSVG}
              onExportDXF={handleExportDXF}
              exportOptions={exportOptions}
              onExportOptionsChange={setExportOptions}
              bboxAreaKm2={bboxAreaKm2}
              largeAreaKm2={largeAreaKm2}
              hasData={hasData}
            />
            {exportError && <div className="qsp-status qsp-status--error" style={{ margin: '14px 0 0' }}>{exportError}</div>}
          </div>
        )}
      </div>

      <div className="qsp-section">
        <button type="button" className="qsp-section__head" onClick={() => setStyleOpen(s => !s)} aria-expanded={styleOpen}>
          <span className="qsp-section__title">{t('styleTitle')}</span>
          <span className={`qsp-section__chevron${styleOpen ? ' qsp-section__chevron--open' : ''}`}>&#9656;</span>
        </button>
        {styleOpen && (
          <div className="qsp-section__body">
            <StylePanel
              selectedTypes={selectedTypes}
              preset={stylePreset}
              onPresetChange={onStylePresetChange}
              layerStyles={layerStyles}
              onLayerStyleChange={onLayerStyleChange}
            />
          </div>
        )}
      </div>

      <div className="qsp-section">
        <button type="button" className="qsp-section__head" onClick={() => setStepsOpen(s => !s)} aria-expanded={stepsOpen}>
          <span className="qsp-section__title">{t('howItWorksTitle')}</span>
          <span className={`qsp-section__chevron${stepsOpen ? ' qsp-section__chevron--open' : ''}`}>&#9656;</span>
        </button>
        {stepsOpen && (
          <div className="qsp-section__body qsp-steps-body">
            <ol>
              <li><span className="qsp-step-num">1</span> {t('step1')}</li>
              <li><span className="qsp-step-num">2</span> {t('step2')}</li>
              <li><span className="qsp-step-num">3</span> {t('step3')}</li>
              <li><span className="qsp-step-num">4</span> {t('step4')}</li>
            </ol>
          </div>
        )}
      </div>

      <div className="qsp-footer">
        <p>{t('disclaimerShort')}</p>
        <p>
          © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>
          {' · '}
          <button type="button" onClick={() => setLegalPage('imprint')}>{t('imprintTitle')}</button>
          {' · '}
          <button type="button" onClick={() => setLegalPage('privacy')}>{t('privacyTitle')}</button>
        </p>
      </div>
      {legalPage && <LegalModal page={legalPage} onClose={() => setLegalPage(null)} />}
    </div>
  );
};

export default SidebarCollapsible;
